"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PopoverRoot = exports.PopoverStandalone = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_2 = require("@floating-ui/react");
const lodash_es_1 = require("lodash-es");
const useToggle_1 = __importDefault(require("../../utils/hooks/useToggle"));
const usePrevious_1 = __importDefault(require("../../utils/hooks/usePrevious"));
const config_1 = require("../../config");
const PopoverRootContext = (0, react_1.createContext)(null);
const delayed = (middleware) => ({
    name: middleware.name,
    options: middleware.options,
    fn: (state) => new Promise((resolve) => {
        const result = middleware.fn(state);
        setTimeout(() => {
            resolve(result);
        }, 5);
    }),
});
const Popover = ({ children, content, visible: propsVisible = false, width, gap = 0, clickOutsideToHide, onShow, onHide, toggle = true, placement: propsPlacement = 'bottom', hover = false, arrow: arrowProps, }) => {
    const popoverRoot = (0, react_1.useContext)(PopoverRootContext);
    const floatingParentNodeId = (0, react_2.useFloatingParentNodeId)();
    const nodeId = (0, react_2.useFloatingNodeId)(floatingParentNodeId);
    const handleVisibleChange = (0, react_1.useCallback)((value) => {
        if (value) {
            return onShow && onShow();
        }
        return onHide && onHide();
    }, [onShow, onHide]);
    const [visible, toggleVisible, setVisible] = (0, useToggle_1.default)(propsVisible, handleVisibleChange);
    const prevPropsVisible = (0, usePrevious_1.default)(propsVisible);
    (0, react_1.useEffect)(() => {
        // handle of direct prop changing
        if (prevPropsVisible !== null && propsVisible !== prevPropsVisible) {
            setVisible(propsVisible);
        }
    }, [propsVisible, setVisible]);
    const handleHide = (0, react_1.useCallback)(() => setVisible(false), [setVisible]);
    const handleShow = (0, react_1.useCallback)(() => setVisible(true), [setVisible]);
    const arrowRef = (0, react_1.useRef)(null);
    const { context, floatingStyles, refs, placement, middlewareData } = (0, react_2.useFloating)({
        placement: propsPlacement,
        nodeId,
        open: visible,
        onOpenChange: setVisible,
        whileElementsMounted: (reference, floating, update) => {
            const node = popoverRoot?.id
                ? document.getElementById(popoverRoot.id)
                : null;
            const { basePopoverZIndex } = config_1.UI_KIT_CONFIG;
            const nodeIndex = node
                ? parseFloat(window.getComputedStyle(node).zIndex) || 0
                : 0;
            // eslint-disable-next-line no-param-reassign
            floating.style.zIndex = String(nodeIndex + basePopoverZIndex + 1);
            return (0, react_2.autoUpdate)(reference, floating, update);
        },
        middleware: [
            (0, react_2.size)({
                apply: ({ elements, rects, availableWidth }) => {
                    const widthValue = width === 'parent' ? rects.reference.width : width;
                    if (widthValue) {
                        const calculatedWidthValue = typeof widthValue === 'number'
                            ? Math.min(availableWidth, widthValue)
                            : widthValue;
                        Object.assign(elements.floating.style, {
                            width: `${width === 'parent' ? widthValue : calculatedWidthValue}px`,
                        });
                    }
                },
            }),
            (0, react_2.offset)(gap),
            delayed((0, react_2.flip)({ elementContext: 'floating' })), // flip if floating doesn't fit'
            delayed((0, react_2.flip)({ padding: gap, elementContext: 'reference' })), // flip if reference in gap px
            (0, react_2.shift)({ padding: gap }),
            (0, react_2.hide)({
                strategy: 'referenceHidden',
                elementContext: 'reference',
                rootBoundary: 'document',
                padding: popoverRoot?.padding,
            }),
            (0, react_2.arrow)({ element: arrowRef, padding: gap }),
        ],
    });
    const { getReferenceProps, getFloatingProps } = (0, react_2.useInteractions)([
        (0, react_2.useClick)(context, {
            enabled: !hover,
            toggle: toggle && !propsVisible, // do not toggle while visible={true}
        }),
        (0, react_2.useDismiss)(context, {
            enabled: !propsVisible, // do nothing while visible={true}
            outsidePress: clickOutsideToHide,
            outsidePressEvent: 'click',
        }),
        (0, react_2.useHover)(context, {
            enabled: hover && !propsVisible, // do nothing dismiss on mouseleave while visible={true}
        }),
    ]);
    const WrapComponent = floatingParentNodeId === null ? react_2.FloatingTree : react_1.Fragment;
    const { referenceHidden } = middlewareData?.hide || {};
    const popoverContent = !referenceHidden && content({ onHide: handleHide, placement });
    return ((0, jsx_runtime_1.jsxs)(WrapComponent, { children: [children({
                ref: refs.setReference,
                props: getReferenceProps(),
                onShow: handleShow,
                onHide: handleHide,
                onToggle: toggleVisible,
                visible,
            }), (0, jsx_runtime_1.jsx)(react_2.FloatingNode, { id: nodeId, children: (0, jsx_runtime_1.jsx)(react_2.FloatingPortal, { children: visible && ((0, jsx_runtime_1.jsxs)("div", { ref: refs.setFloating, style: floatingStyles, ...getFloatingProps(), children: [popoverContent && arrowProps && ((0, jsx_runtime_1.jsx)(react_2.FloatingArrow, { ref: arrowRef, context: context, ...arrowProps })), popoverContent] })) }) })] }));
};
exports.default = Popover;
const PopoverStandalone = ({ children, onShow, onHide, visible, clickOutsideToHide = true, }) => {
    const floatingParentNodeId = (0, react_2.useFloatingParentNodeId)();
    const nodeId = (0, react_2.useFloatingNodeId)(floatingParentNodeId);
    const handleVisibleChange = (0, react_1.useCallback)((value) => {
        if (value) {
            return onShow && onShow();
        }
        return onHide && onHide();
    }, [onShow, onHide]);
    const { context, refs } = (0, react_2.useFloating)({
        nodeId,
        open: visible,
        onOpenChange: handleVisibleChange,
        whileElementsMounted: react_2.autoUpdate,
    });
    const { getFloatingProps } = (0, react_2.useInteractions)([
        (0, react_2.useDismiss)(context, { outsidePress: clickOutsideToHide }),
    ]);
    const content = ((0, jsx_runtime_1.jsx)(react_2.FloatingNode, { id: nodeId, children: (0, jsx_runtime_1.jsx)(react_2.FloatingPortal, { children: visible && ((0, jsx_runtime_1.jsx)("div", { ref: refs.setFloating, ...getFloatingProps(), children: children({ onHide }) })) }) }));
    if (!floatingParentNodeId) {
        return (0, jsx_runtime_1.jsx)(react_2.FloatingTree, { children: content });
    }
    return content;
};
exports.PopoverStandalone = PopoverStandalone;
exports.PopoverRoot = (0, react_1.forwardRef)(({ tagName: TagName = 'div', id, padding, ...props }, ref) => {
    const containerIdRef = (0, react_1.useRef)(id || (0, lodash_es_1.uniqueId)('ui-popover-root-'));
    const value = (0, react_1.useMemo)(() => ({ padding, id: containerIdRef.current }), [padding]);
    return ((0, jsx_runtime_1.jsx)(PopoverRootContext.Provider, { value: value, children: (0, jsx_runtime_1.jsx)(TagName, { ref: ref, ...props, id: containerIdRef.current }) }));
});
